import React, { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DNABox, DNASelect, DNAText, Iffy, IndexPath } from '@alucio/lux-ui';
import { Attendee, CustomFieldDefinition } from '@alucio/aws-beacon-amplify/src/models';
import { ATTENDEE_TYPE, MEETING_ATTENDEE_STATUS } from '@alucio/aws-beacon-amplify/src/API';
import { CRMAccount } from 'src/classes/CRM/CRMIndexedDBTypes';
import { Singleton as IndexDbCrm } from 'src/classes/CRM/CRMIndexedDB';
import { FormValuesType, useComposableForm } from '../CustomFields/ComposableForm';
import { toORMAccount } from 'src/screens/Profile/CRMIntegration';
import { CRMAccountORM } from 'src/types/orms';
import { ObjectWithId } from '../CustomFields/ComposableFormUtilities';
import * as logger from 'src/utils/logger'
import { SUPPORTED_VEEVA_CONTROL_TYPES } from 'src/classes/CRM/CRMVeevaVaultTypes';

const VeevaVaultLocationWrapper = () => {
  const { customFields, rhForm, isReadOnly } = useComposableForm();
  const primaryAttendee = rhForm.watch('primaryAttendee');

  // CHECKS IF business_account__v IS PRESENT
  const businessAccountCustomField = useMemo(() => customFields
    .find(({ id }) => id === SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT), [customFields]);

  if (!businessAccountCustomField || !primaryAttendee?.length) {
    return null;
  }

  return (
    <DNABox appearance="col" fill>
      <VeevaVaultLocationComponent
        isReadOnly={isReadOnly}
        field={businessAccountCustomField}
        rhForm={rhForm}
      />
    </DNABox>
  );
};

interface LocationProps {
  rhForm: UseFormReturn<FormValuesType>
  isReadOnly?: boolean
  field: CustomFieldDefinition
}

// VEEVA SPECIFIC LOCATION'S FIELD
const VeevaVaultLocationComponent = (props: LocationProps) => {
  const { field, isReadOnly, rhForm } = props;
  const [primaryAttendee, additionalAttendees] =
    rhForm.watch(['primaryAttendee', 'additionalAttendees']) as unknown as [Attendee[], Attendee[]];
  const selectedBusinessAccount = additionalAttendees
    .find(({ id }) => id === SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT)?.crmAccountId;
  const [businessAccounts, setBusinessAccounts] = useState<CRMAccountORM[]>([]);
  const index = businessAccounts.findIndex((account) => account.model.id === selectedBusinessAccount);
  const selectIdx = index > -1 ? new IndexPath(index) : undefined;
  const [isPersonAccount, setIsPersonAccount] = useState<boolean>(false);

  useEffect(() => {
    // AFTER SELECTING A PRIMARY ATTENDEE, GETS ITS BUSINESS ACCOUNTS TO UPDATE THE SELECT ITEMS
    const handlePrimaryAttendeeUpdate = async (): Promise<void> => {
      // REMOVES THE BUSINESS ACCOUNT FROM PREVIOUS SELECTION
      const filteredAttendees =
        additionalAttendees.filter(({ id }) => id !== SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT);
      let shouldResetFilteredAttendees = true;

      const crmAccountId = primaryAttendee[0]?.crmAccountId;
      if (crmAccountId) {
        const account = await IndexDbCrm.getById<CRMAccount>('ACCOUNT', crmAccountId);
        if (account?.ispersonaccount__v) {
          setIsPersonAccount(true);
          const businessAccountId =
            typeof account.primary_parent__v === 'string' ? account.primary_parent__v : '';

          // BUSINESS ACCOUNTS IDS OF THE PERSON ACCOUNT TO LATER GET THEM ON INDEXDB
          const businessAccountsRaw = account.child_account__vr?.['data'];
          const accountsIds = !Array.isArray(businessAccountsRaw)
            ? [businessAccountId]
            : businessAccountsRaw.reduce((acc, businessAccount) =>
              businessAccount.parent_account__v ? [...acc, businessAccount.parent_account__v] : acc, []);

          // GETS THE BUSINESS ACCOUNTS OF THE PERSON ACCOUNT
          const businessAccounts = await IndexDbCrm.filterById('ACCOUNT', accountsIds);
          const config = await IndexDbCrm.getConfig();
          const accountsORM = toORMAccount(businessAccounts, config);
          // THE BUSINESS ACCOUNTS MUST HAVE "do_not_call__v" SET TO "no__v"
          const filteredAccountsORMS = accountsORM
            .filter((accountORM) => accountORM.model?.do_not_call__v?.toString() === 'no__v');

          const predefinedBusinessAccountIndex = filteredAccountsORMS.findIndex((accountORM) =>
            accountORM.model?.id === businessAccountId);
          const isSelectedBusinessAccountValid = filteredAccountsORMS.some((accountORM) =>
            accountORM.model.id === selectedBusinessAccount);
          const shouldSetADefaultBusinessAccount = !isSelectedBusinessAccountValid &&
            (predefinedBusinessAccountIndex >= 0 || (field.required && filteredAccountsORMS.length));

          if (shouldSetADefaultBusinessAccount) {
            const newValue = [...filteredAttendees, {
              id: SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT,
              crmAccountId: filteredAccountsORMS[predefinedBusinessAccountIndex || 0]?.model.id,
              attendeeType: ATTENDEE_TYPE.SECONDARY,
              name: filteredAccountsORMS[predefinedBusinessAccountIndex || 0]?.meta.formattedName,
              status: MEETING_ATTENDEE_STATUS.ACTIVE,
              updatedAt: new Date().toISOString(),
            }] as unknown as ObjectWithId[];

            shouldResetFilteredAttendees = false;
            rhForm.setValue('additionalAttendees', newValue);
          } else if (isSelectedBusinessAccountValid) {
            shouldResetFilteredAttendees = false;
          } else if (!filteredAccountsORMS.length) {
            logger.salesforceSyncer.debug('Business Accounts not found...');
          }

          setBusinessAccounts(filteredAccountsORMS);
          if (shouldResetFilteredAttendees) {
            rhForm.setValue('additionalAttendees', filteredAttendees as unknown as ObjectWithId[]);
          }
          return;
        } else {
          setIsPersonAccount(false);
        }
      }
      setBusinessAccounts([]);
      rhForm.setValue('additionalAttendees', filteredAttendees as unknown as ObjectWithId[]);
    }
    handlePrimaryAttendeeUpdate();
  }, [primaryAttendee]);

  if (!isPersonAccount) {
    return null;
  }

  const onSelectBusinessAccount = (index : IndexPath | IndexPath[]) => {
    const idx = index as IndexPath
    const businessAccount = businessAccounts[idx.row];
    if (businessAccount?.model.id !== selectedBusinessAccount) {
      const remainingAttendees = (additionalAttendees as unknown as Attendee[])?.filter(({ id }) =>
        id !== SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT);
      const newValue = [...remainingAttendees, {
        id: SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT,
        crmAccountId: businessAccount.model.id,
        attendeeType: ATTENDEE_TYPE.SECONDARY,
        name: businessAccount.meta.formattedName,
        status: MEETING_ATTENDEE_STATUS.ACTIVE,
        updatedAt: new Date().toISOString(),
      } as Attendee] as unknown as ObjectWithId[];
      rhForm.setValue('additionalAttendees', newValue);
    }
  };

  return (
    <DNABox>
      <Iffy is={!businessAccounts.length}>
        <DNABox appearance="col" fill spacing="sm">
          <DNAText bold>Location</DNAText>
          <DNAText status="subtle">No locations were found</DNAText>
        </DNABox>
      </Iffy>
      <Iffy is={businessAccounts.length}>
        <DNABox appearance="col" fill spacing="sm">
          <DNAText bold status="primary">Location</DNAText>
          <DNASelect
            disabled={isReadOnly}
            onSelect={onSelectBusinessAccount}
            selectedIndex={selectIdx}
            value={index > -1 ? businessAccounts[index].meta.formattedName : 'Select location'}
          >
            {businessAccounts?.map((businessAccount) => (
              <DNASelect.Item
                key={businessAccount.model.id}
                title={() =>
                  (<DNABox appearance="col" spacing="sm">
                    <DNAText c1 bold numberOfLines={1}>
                      {businessAccount.meta.formattedName}
                    </DNAText>
                    <DNAText c2 status="subtle">
                      {businessAccount.relations.addresses[0]?.meta.formattedName}
                    </DNAText>
                  </DNABox>)}
              />
            ))}
          </DNASelect>
        </DNABox>
      </Iffy>
    </DNABox>
  )
};

export default VeevaVaultLocationWrapper;
